/**
 * Role Permissions Page
 * Main page for linking permissions to roles using a visual permission matrix
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Role } from '../../types/rbac.types';
import { getRoles, getRoleById, syncRolePermissions } from '../../services/roleService';
import PermissionAccordionMatrix from '../../components/rbac/PermissionAccordionMatrix';
import { PageShell } from '../../components';

/** Extrai a mensagem de erro da resposta da API, preservando o fallback. */
const getApiErrorMessage = (err: unknown, fallback: string): string => {
  const apiError = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof apiError === 'string' && apiError ? apiError : fallback;
};

const RolePermissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();

  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Track the saved permission ids to detect changes
  const savedPermissionIdsRef = useRef<number[]>([]);

  const loadRoles = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRoles({ isActive: true }, { page: 1, limit: 100 });
      setRoles(response.data);
    } catch (err: unknown) {
      console.error('Error loading roles:', err);
      setError(getApiErrorMessage(err, 'Erro ao carregar funções'));
      enqueueSnackbar('Erro ao carregar funções', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadRoleById = useCallback(async (roleId: number): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRoleById(roleId);
      setSelectedRole(response.data);
      const permIds = response.data.permissions?.map((p) => p.id) ?? [];
      setSelectedPermissionIds(permIds);
      savedPermissionIdsRef.current = permIds;
      setHasChanges(false);
    } catch (err: unknown) {
      console.error('Error loading role:', err);
      setError(getApiErrorMessage(err, 'Erro ao carregar função'));
      enqueueSnackbar('Erro ao carregar função', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (id && roles.length > 0) {
      loadRoleById(parseInt(id));
    }
  }, [id, roles, loadRoleById]);

  const handleRoleChange = (_event: React.SyntheticEvent, newValue: Role | null): void => {
    if (newValue) {
      navigate(`/roles/${newValue.id}/permissions`);
    } else {
      setSelectedRole(null);
      setSelectedPermissionIds([]);
      savedPermissionIdsRef.current = [];
      setHasChanges(false);
    }
  };

  const handlePermissionsChange = (permissionIds: number[]): void => {
    setSelectedPermissionIds(permissionIds);
    // Detect if changed from saved state
    const savedSet = new Set(savedPermissionIdsRef.current);
    const newSet = new Set(permissionIds);
    const changed =
      savedSet.size !== newSet.size || [...savedSet].some((id) => !newSet.has(id));
    setHasChanges(changed);
  };

  const handleSave = async (): Promise<void> => {
    if (!selectedRole) {
      enqueueSnackbar('Selecione uma função', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      await syncRolePermissions(selectedRole.id, selectedPermissionIds);
      enqueueSnackbar('Permissões sincronizadas com sucesso!', { variant: 'success' });

      // Reload role to get updated data
      await loadRoleById(selectedRole.id);
    } catch (err: unknown) {
      console.error('Error syncing permissions:', err);
      enqueueSnackbar(
        getApiErrorMessage(err, 'Erro ao sincronizar permissões'),
        { variant: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBack = (): void => {
    navigate('/roles');
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Funções', path: '/roles' },
        {
          label: selectedRole ? selectedRole.displayName : 'Função',
          path: selectedRole ? `/roles/${selectedRole.id}/edit` : '/roles',
        },
        { label: 'Permissões' },
      ]}
      title="Vincular Permissões"
      actions={
        <>
          {hasChanges && (
            <Chip
              icon={<WarningIcon />}
              label="Alterações não salvas"
              color="warning"
              size="small"
            />
          )}
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Voltar para Funções
          </Button>
        </>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
          {/* Role Selection Card */}
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Selecione a Função
                </Typography>
                <Autocomplete
                  options={roles}
                  getOptionLabel={(option) => option.displayName}
                  value={selectedRole}
                  onChange={handleRoleChange}
                  loading={loading}
                  disabled={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Função"
                      placeholder="Selecione uma função"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body1">{option.displayName}</Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                />

                {selectedRole && (
                  <Box mt={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    {selectedRole.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                        {selectedRole.description}
                      </Typography>
                    )}
                    <Chip
                      label={selectedRole.isActive ? 'Ativo' : 'Inativo'}
                      size="small"
                      color={selectedRole.isActive ? 'success' : 'default'}
                    />
                    {selectedRole.isSystemRole && (
                      <Chip label="Sistema" size="small" color="warning" />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Permission Matrix */}
          {selectedRole && (
            <Grid size={12}>
              <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Permissões da Função
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Selecione as permissões que esta função terá acesso
                    </Typography>
                  </Box>
                  <Chip
                    label={`${selectedPermissionIds.length} selecionada${selectedPermissionIds.length !== 1 ? 's' : ''}`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                <PermissionAccordionMatrix
                  selectedPermissionIds={selectedPermissionIds}
                  onChange={handlePermissionsChange}
                  disabled={saving || selectedRole.isSystemRole}
                />

                {selectedRole.isSystemRole && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Funções que vêm com o sistema têm as permissões fixas. Para um conjunto
                    diferente de permissões, crie uma função própria na tela de Funções.
                  </Alert>
                )}

                <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                  <Button variant="outlined" onClick={handleBack} disabled={saving}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || selectedRole.isSystemRole}
                  >
                    {saving ? 'Salvando...' : 'Salvar Permissões'}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          )}
      </Grid>
    </PageShell>
  );
};

export default RolePermissionsPage;
